import { useCallback } from "react";
import { useEpisod } from "../analytics/useEpisod";
import { anchorFromSearch, DEFAULT_ANCHOR } from "../utils/anchor";
import { useHomeCountry } from "./useHomeCountry";
import { useLiveSearchParams } from "./useLiveSearchParams";

/**
 * Param keys that are shared between Top Combinations and Analysis Dashboard.
 * When navigating between pages, these are carried over in the URL.
 */
export const SHARED_PARAM_KEYS = ["anchor", "countries", "sub_markets", "sales_channels", "period", "frame_types"] as const;

export const ALL_FRAME_TYPES = ["regular", "corner", "end", "mid", "height_extension"];
/** When no frame_types URL param is present, default to ALL types (no filter). */
export const DEFAULT_FRAME_TYPES = ALL_FRAME_TYPES;
export { DEFAULT_ANCHOR };

/** Build a query string containing only the shared params from the current URL. */
export function buildSharedQueryString(currentParams: URLSearchParams): string {
    const shared = new URLSearchParams();
    for (const key of SHARED_PARAM_KEYS) {
        const value = currentParams.get(key);
        if (value !== null) shared.set(key, value);
    }
    // Support legacy "country" param from old bookmarks
    if (!shared.has("countries") && currentParams.get("country")) {
        shared.set("countries", currentParams.get("country")!);
    }
    const qs = shared.toString();
    return qs ? `?${qs}` : "";
}

const FAVORITE_COUNTRY_KEY = "mcc-favorite-country";
const FAVORITE_PERIOD_KEY = "mcc-favorite-period";

function getStored(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

export interface SharedParams {
    anchor: string;
    selectedCountries: string[];
    selectedSalesChannels: string[];
    subMarketsParam: string | null;
    period: string;
    frameTypes: string[] | null;
    /**
     * Whether the user's home country has finished resolving. The page-level
     * self-heal guards must wait for this before rewriting the `countries`
     * param, otherwise a late-resolving home country would be silently
     * overridden by an arbitrary "first available" country.
     */
    homeCountryResolved: boolean;
    /**
     * Whether `selectedCountries` came from an explicit URL param (vs. the
     * home-country/favorite/DE default). When the URL carries an explicit
     * (possibly invalid) `countries` value, the self-heal guards can heal
     * immediately — home-country resolution is irrelevant in that case, so
     * they must NOT wait on `homeCountryResolved`.
     */
    countriesFromUrl: boolean;
    updateSharedParam: (key: string, value: string | undefined) => void;
    setAnchor: (value: string) => void;
    setCountries: (selected: string[]) => void;
    setSubMarkets: (selected: string[], allCodes: string[]) => void;
    setSalesChannels: (selected: string[], all: string[]) => void;
    setPeriod: (value: string) => void;
    setFrameTypes: (types: string[]) => void;
}

/**
 * Hook that reads/writes the shared filter params from URL search params.
 * Used by both TopCombinations and Dashboard so navigation preserves state.
 */
export function useSharedParams(allSalesChannels: string[]): SharedParams {
    const [searchParams, setSearchParams] = useLiveSearchParams();
    const { sendAnalyticsEvent } = useEpisod();

    const trackFilter = useCallback(
        (filter: string, value: string, extra?: Record<string, unknown>) => {
            sendAnalyticsEvent({
                category: "Filters",
                action: "Filter changed",
                label: filter,
                custom: { filter, value, ...extra },
            });
        },
        [sendAnalyticsEvent],
    );

    const favoriteCountry = getStored(FAVORITE_COUNTRY_KEY);
    const favoritePeriod = getStored(FAVORITE_PERIOD_KEY);

    // Default Retail Unit precedence: explicit URL param > starred favorite >
    // the user's home country (from their profile) > "DE". If the resolved
    // country has no data for the current anchor, the page-level guard
    // self-heals to the first available country.
    const { home: homeCountry, resolved: homeCountryResolved } = useHomeCountry();
    const defaultCountry = favoriteCountry || homeCountry || "DE";
    const countriesParam = searchParams.get("countries") ?? searchParams.get("country");
    const countriesFromUrl = countriesParam !== null;
    const selectedCountries = countriesFromUrl
        ? countriesParam!.split(",").filter(Boolean)
        : [defaultCountry];

    const salesChannelsParam = searchParams.get("sales_channels");
    const selectedSalesChannels = salesChannelsParam !== null
        ? salesChannelsParam.split(",").filter(Boolean)
        : allSalesChannels;

    const subMarketsParam = searchParams.get("sub_markets");
    const period = searchParams.get("period") || favoritePeriod || "8w";
    const frameTypes = searchParams.has("frame_types")
        ? searchParams.get("frame_types")!.split(",").filter(Boolean)
        : null;
    const anchor = anchorFromSearch(searchParams.toString());

    const updateSharedParam = useCallback(
        (key: string, value: string | undefined) => {
            setSearchParams((prev) => {
                if (value === undefined || value === "") {
                    prev.delete(key);
                } else {
                    prev.set(key, value);
                }
                return prev;
            });
        },
        [setSearchParams],
    );

    const setCountries = useCallback(
        (selected: string[]) => {
            setSearchParams((prev) => {
                prev.delete("country"); // remove legacy key
                prev.set("countries", selected.join(","));
                prev.delete("sub_markets"); // reset dependent filter
                return prev;
            });
            trackFilter("countries", selected.join(","));
        },
        [setSearchParams, trackFilter],
    );

    const setSubMarkets = useCallback(
        (selected: string[], allCodes: string[]) => {
            setSearchParams((prev) => {
                if (selected.length === allCodes.length) {
                    prev.delete("sub_markets");
                } else {
                    prev.set("sub_markets", selected.join(","));
                }
                return prev;
            });
            trackFilter("sub_markets", selected.length === allCodes.length ? "all" : selected.join(","));
        },
        [setSearchParams, trackFilter],
    );

    const setSalesChannels = useCallback(
        (selected: string[], all: string[]) => {
            setSearchParams((prev) => {
                if (selected.length === all.length) {
                    prev.delete("sales_channels");
                } else {
                    prev.set("sales_channels", selected.join(","));
                }
                return prev;
            });
            trackFilter("sales_channels", selected.length === all.length ? "all" : selected.join(","));
        },
        [setSearchParams, trackFilter],
    );

    const setPeriod = useCallback(
        (value: string) => {
            updateSharedParam("period", value);
            trackFilter("period", value);
        },
        [updateSharedParam, trackFilter],
    );

    const setFrameTypes = useCallback(
        (types: string[]) => {
            setSearchParams((prev) => {
                if (types.length === 0) {
                    prev.delete("frame_types");
                } else {
                    prev.set("frame_types", types.join(","));
                }
                return prev;
            });
            trackFilter("frame_types", types.length === 0 ? "all" : types.join(","));
        },
        [setSearchParams, trackFilter],
    );

    const setAnchor = useCallback(
        (value: string) => {
            // Read before navigating: afterwards the URL holds the new anchor.
            const fromAnchor = anchorFromSearch(window.location.search);
            setSearchParams((prev) => {
                const v = (value || DEFAULT_ANCHOR).toUpperCase();
                // Strip all non-shared params — cross-filters (door_solution,
                // color, tab, etc.) are anchor-specific and shouldn't carry over.
                const kept = new URLSearchParams();
                for (const key of SHARED_PARAM_KEYS) {
                    const val = prev.get(key);
                    if (val !== null) kept.set(key, val);
                }
                if (v === DEFAULT_ANCHOR) {
                    kept.delete("anchor");
                } else {
                    kept.set("anchor", v);
                }
                return kept;
            });
            trackFilter("anchor", (value || DEFAULT_ANCHOR).toUpperCase(), { from_anchor: fromAnchor });
        },
        [setSearchParams, trackFilter],
    );

    return {
        anchor,
        selectedCountries,
        selectedSalesChannels,
        subMarketsParam,
        period,
        frameTypes,
        homeCountryResolved,
        countriesFromUrl,
        updateSharedParam,
        setAnchor,
        setCountries,
        setSubMarkets,
        setSalesChannels,
        setPeriod,
        setFrameTypes,
    };
}
